"use client";

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Cable,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Clock3,
  Database,
  Eye,
  Gauge,
  KeyRound,
  LoaderCircle,
  Mail,
  Radio,
  RefreshCw,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GithubPemCredentialInput } from "./github-pem-credential-input";
import { resolveRunyiStatus, runyiStatusProgressClasses, runyiStatusRowClasses, runyiStatusTextClasses, runyiStatusToneClasses } from "@/lib/status-indicator";
import { UniversalInfrastructureActions } from "./universal-infrastructure-actions";
import { ManualMembersWorkspace } from "./manual-members-workspace";
import { UniversalAccountsSetupPanel } from "./universal-accounts-setup-panel";

export type NewInfrastructureCredentialField = { key:string; label:string; type:"text"|"secret"|"secret_textarea"; required?:boolean };

export type NewInfrastructureLiveRequirement = {
  requirement_key:string;
  requirement_type:string;
  label:string;
  state:string;
  required:boolean;
  action_kind?:string | null;
  action_payload?:Record<string,any>;
  satisfied_reference?:string | null;
  verification?:Record<string,any>;
};

export type NewInfrastructureLiveSetup = {
  setup_id:string;
  setup_key:string;
  provider_capability_key:string;
  provider_key:string;
  display_name:string;
  lifecycle_state:string;
  backend_stage:string;
  live_stage:string;
  status_summary:string;
  live_summary:string;
  progress_percent:number;
  human_action_required:boolean;
  action_kind?:string | null;
  setup_session_id?:string | null;
  target_account_ref?:string | null;
  target_member_email?:string | null;
  target_project_ref?:string | null;
  metadata?:Record<string,any>;
  last_backend_event?:string | null;
  last_verified_at?:string | null;
  created_by_channel?:string | null;
  created_at:string;
  updated_at:string;
  capability?:{
    provider_capability_key:string;
    provider_key:string;
    display_name:string;
    service_type:string;
    logical_capability_hint?:string | null;
    state:string;
    risk_level?:string | null;
    approval_required?:boolean;
    metadata?:Record<string,any>;
  } | null;
  profile?:{
    profile_key:string;
    provider_capability_key:string;
    provider_key:string;
    logical_capability:string;
    resource_type?:string | null;
    auth_methods:string[];
    required_connection_capabilities:string[];
    credential_schema?:{fields?:Array<Record<string,any>>};
    configuration_schema?:{fields?:Array<Record<string,any>>};
    updated_at?:string | null;
  } | null;
  connection?:{
    connection_key:string;
    display_name:string;
    auth_mode:string;
    account_ref?:string | null;
    project_ref?:string | null;
    health_state:string;
    last_verified_at?:string | null;
  } | null;
  missing_permissions?:string[];
  project_options?:Array<{
    ref:string;
    id?:string|null;
    name?:string|null;
    region?:string|null;
    status?:string|null;
    organization_id?:string|null;
  }>;
  organization_options?:Array<{
    slug:string;
    id?:string|null;
    name?:string|null;
  }>;
  execution?:{
    profile_key?:string | null;
    coverage_state:string;
    action_key?:string | null;
    blueprint_key?:string | null;
    dispatch_strategy?:string | null;
    verification_profile?:string | null;
    updated_at?:string | null;
  } | null;
  digital_display?:{
    overall_state:string;
    progress_percent:number;
    human_action_required:boolean;
    next_human_action?:{kind?:string|null;label?:string|null}|null;
    backend_action_active:boolean;
    backend_action_description?:string|null;
    stages:{
      backend_definition:string;
      authentication:string;
      credential:string;
      permissions:string;
      provider_connection:string;
      execution_adapter:string;
      verification:string;
      marketplace:string;
    };
  };
  interaction_contract?:{
    version:string;
    instance?:{provider_key:string;account_email?:string|null;project_ref?:string|null};
    authorization?:{
      required:boolean;state:string;auth_methods:string[];human_action?:string|null;
      browser?:{mode:string;callback:string;adapter_registered:boolean}|null;
      reuse_valid_authorization:boolean;
    };
    email_verification?:{
      adapter:string;email:string;project_ref:string;selected_method:string;
      methods:Array<{kind:"LINK"|"CODE";availability:string;requires_email_channel:boolean}>;
      numeric_otp_length_range:number[];
    }|null;
    configuration?:{source:string;profile_key?:string|null;fields?:Array<{key:string;label:string;type:string;required:boolean}>};
    activation?:{provider_connection_verified:boolean;infrastructure_verified:boolean;member_identity_verified:boolean;marketplace_ready:boolean;human_action_required:boolean;next_action?:string|null};
  };
  provider_truth?:{
    state:string;
    connection_key?:string|null;
    account_ref?:string|null;
    project_ref?:string|null;
    auth_mode?:string|null;
    last_verified_at?:string|null;
    source:string;
    intended_account_ref?:string|null;
    intended_project_ref?:string|null;
  };
  secret_values_exposed:false;
};

export type NewInfrastructureLiveData = {
  architecture_version:string;
  live:boolean;
  setups:NewInfrastructureLiveSetup[];
  activity:Array<{
    event_id:string | number;
    event_type:string;
    actor?:string|null;
    summary:string;
    provider_capability_key?:string | null;
    backend_stage?:string | null;
    created_at:string;
  }>;
  digital_display?:{
    state:string;
    backend_online:boolean;
    realtime_source:string;
    fallback_poll_ms:number;
    last_sync_at:string;
    active_setups?:number;
    human_action_count?:number;
    backend_working_count?:number;
    verified_count?:number;
  };
  secret_values_exposed:false;
  refresh_after_ms:number;
};

type AppItem={entity_key:string;display_name:string};

function pretty(value:string | null | undefined) {
  return String(value ?? "").replaceAll("_"," ").replace(/\b\w/g,(letter)=>letter.toUpperCase());
}

function stateVisual(value:string | null | undefined) {
  const tone=resolveRunyiStatus(value).tone;
  return {
    badge:runyiStatusToneClasses[tone],
    row:runyiStatusRowClasses[tone],
    status:runyiStatusTextClasses[tone],
    progress:runyiStatusProgressClasses[tone],
  };
}

function stateTone(value:string | null | undefined) {
  return stateVisual(value).badge;
}

function statusIcon(value:string | null | undefined) {
  const tone=resolveRunyiStatus(value).tone;
  if (tone==="success") return CheckCircle2;
  if (tone==="danger" || tone==="warning" || tone==="action") return CircleAlert;
  if (tone==="live") return Radio;
  if (tone==="ai") return Sparkles;
  return Clock3;
}

function relativeTime(value:string | null | undefined) {
  if (!value) return "—";
  const time=new Date(value).getTime();
  if (!Number.isFinite(time)) return "—";
  const seconds=Math.max(0,Math.floor((Date.now()-time)/1000));
  if (seconds<10) return "just now";
  if (seconds<60) return `${seconds}s ago`;
  if (seconds<3600) return `${Math.floor(seconds/60)}m ago`;
  return new Date(value).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
}

export function NewInfrastructureLiveConsole({
  data,busy,liveConnected,role,supabase,connected,applications,onOpenSetup,onContinueSetup,onSendVerification,onRefresh,onMarketplace,onAddMember,onRegisterExisting,
}:{
  data:NewInfrastructureLiveData | null;
  busy:boolean;
  liveConnected:boolean;
  role?:"OWNER_ADMIN" | "ADMIN" | "MEMBER";
  supabase:SupabaseClient | null;
  connected:boolean;
  applications:AppItem[];
  onOpenSetup:(setupId:string)=>void;
  onContinueSetup:(setupId:string)=>void;
  onSendVerification:(setupId:string,method:"CODE"|"LINK")=>void;
  onRefresh:()=>void;
  onMarketplace:()=>void;
  onAddMember:()=>void;
  onRegisterExisting:()=>void;
}) {
  const setups=data?.setups ?? [];
  const display=data?.digital_display;
  // Selected infrastructure is UI navigation state, not backend update order.
  const [selectedSetupId,setSelectedSetupId]=useState<string|null>(null);
  const [selectionRestored,setSelectionRestored]=useState(false);
  const [defaultChosen,setDefaultChosen]=useState(false);
  const [membersOpen,setMembersOpen]=useState(false);
  const selectionStorageKey="runyi:new-infrastructure:selected-setup:v2";
  useEffect(()=>{
    if(typeof window==="undefined") return;
    try {
      const saved=window.localStorage.getItem(selectionStorageKey);
      if(saved!==null) {
        setSelectedSetupId(saved==="__ALL__"?null:saved);
        setMembersOpen(saved==="__ALL__");
      }
    } catch { /* Local storage may be unavailable in WebView. */ }
    setSelectionRestored(true);
  },[]);
  useEffect(()=>{
    if(!selectionRestored||!setups.length||defaultChosen||typeof window==="undefined") return;
    try {
      if(window.localStorage.getItem(selectionStorageKey)!==null) {
        setDefaultChosen(true);
        return;
      }
    } catch { /* Continue with a one-time in-memory selection. */ }
    // One-time upgrade default is the newest CREATED infrastructure (MEMBER 4
    // in the present registry), never the first volatile updated_at result.
    const newest=[...setups].sort((a,b)=>
      new Date(b.created_at).getTime()-new Date(a.created_at).getTime()
      || a.setup_id.localeCompare(b.setup_id)
    )[0];
    if(newest) {
      setSelectedSetupId(newest.setup_id);
      setDefaultChosen(true);
      try { window.localStorage.setItem(selectionStorageKey,newest.setup_id); } catch { /* nonpersistent fallback */ }
    }
  },[selectionRestored,setups,defaultChosen]);
  const selectSetup=(setupId:string|null)=>{
    setDefaultChosen(true);
    setSelectedSetupId(setupId);
    setMembersOpen(setupId===null);
    try {window.localStorage.setItem(selectionStorageKey,setupId??"__ALL__");} catch { /* nonpersistent fallback */ }
  };
  const focused=selectedSetupId
    ? setups.find(item=>item.setup_id===selectedSetupId)??null
    : null;
  const missingSelectedSetup=selectionRestored&&Boolean(selectedSetupId)&&!focused&&setups.length>0;
  const [systemDetailsOpen,setSystemDetailsOpen]=useState(false);
  const [setupDetailsOpen,setSetupDetailsOpen]=useState(false);
  const [otpCode,setOtpCode]=useState("");
  const [otpSubmitting,setOtpSubmitting]=useState(false);
  const [otpError,setOtpError]=useState<string|null>(null);
  const [otpNotice,setOtpNotice]=useState<string|null>(null);
  const [confirmationLink,setConfirmationLink]=useState("");
  const [confirmationSubmitting,setConfirmationSubmitting]=useState(false);
  const [confirmationError,setConfirmationError]=useState<string|null>(null);
  const [verificationMethod,setVerificationMethod]=useState<"LINK"|"CODE">("LINK");

  useEffect(() => {
    if (!systemDetailsOpen || typeof window === "undefined") return;
    const scrollY=window.scrollY;
    const body=document.body;
    const previous={overflow:body.style.overflow,position:body.style.position,top:body.style.top,left:body.style.left,right:body.style.right,width:body.style.width};
    body.style.overflow="hidden"; body.style.position="fixed"; body.style.top=`-${scrollY}px`; body.style.left="0"; body.style.right="0"; body.style.width="100%";
    const closeTop=()=>setSystemDetailsOpen(false);
    const handleBack=(event:Event)=>{event.preventDefault();event.stopImmediatePropagation();closeTop();};
    const handleKey=(event:KeyboardEvent)=>{if(event.key==="Escape"){event.preventDefault();closeTop();}};
    window.addEventListener("runyi:request-back",handleBack as EventListener,{capture:true});
    window.addEventListener("keydown",handleKey);
    return()=>{body.style.overflow=previous.overflow;body.style.position=previous.position;body.style.top=previous.top;body.style.left=previous.left;body.style.right=previous.right;body.style.width=previous.width;window.scrollTo({top:scrollY,left:0,behavior:"auto"});window.removeEventListener("runyi:request-back",handleBack as EventListener,{capture:true});window.removeEventListener("keydown",handleKey);};
  },[systemDetailsOpen]);

  const stages=focused?.digital_display?.stages;
  const progress=focused?.digital_display?.progress_percent ?? focused?.progress_percent ?? 0;
  const focusedVisual=focused
    ? focused.human_action_required
      ? stateVisual("ACTION_REQUIRED")
      : focused.digital_display?.backend_action_active
        ? stateVisual("BACKEND_WORKING")
        : stateVisual(focused.live_stage)
    : stateVisual("UNKNOWN");
  const actionLabel=focused?.digital_display?.next_human_action?.label
    ?? (focused?.human_action_required ? "Continue Authentication" : null);
  const otpAction=String(focused?.action_kind ?? "").toUpperCase();
  const marketplaceAction=otpAction==="RECONCILE_MARKETPLACE";
  const otpRequired=["ENTER_CENTRAL_OTP","ENTER_MEMBER_OTP"].includes(otpAction);
  const emailChannelRequired=otpAction==="CONFIGURE_MEMBER_EMAIL_CHANNEL";
  const confirmationLinkRequired=otpAction==="VERIFY_MEMBER_CONFIRMATION_LINK";
  // Email-method availability describes the provider, not the current step.
  // Do not offer another link/OTP after verified member enrollment when
  // the backend requests only marketplace reconciliation.
  const memberVerification=["MEMBER_TENANT_ENROLLMENT","VERIFY_MEMBER_CONFIRMATION_LINK","ENTER_MEMBER_OTP","CONFIGURE_MEMBER_EMAIL_CHANNEL"].includes(otpAction)
    ? focused?.interaction_contract?.email_verification ?? null
    : null;
  const memberCodePending=otpAction==="ENTER_MEMBER_OTP";
  const otpPhase=otpAction==="ENTER_MEMBER_OTP" ? "MEMBER" : "CENTRAL";

  useEffect(()=>{
    setOtpCode("");
    setOtpError(null);
    setOtpNotice(null);
    setConfirmationLink("");
    setConfirmationError(null);
  },[focused?.setup_id,otpAction]);
  useEffect(()=>{
    setVerificationMethod(
      focused?.interaction_contract?.email_verification?.selected_method==="CODE" ? "CODE" : "LINK"
    );
  },[focused?.setup_id]);

  const submitMemberOtp=async()=>{
    if(!supabase || !connected || !focused || !otpRequired) {
      setOtpError("RUNYI connection is not ready.");
      return;
    }
    const code=otpCode.replace(/\D/g,"").slice(0,8);
    if(code.length<6 || code.length>8) {
      setOtpError("Enter the complete 6- to 8-digit code.");
      return;
    }
    setOtpSubmitting(true);
    setOtpError(null);
    setOtpNotice(null);
    try {
      const {data:payload,error}=await supabase.functions.invoke("runyi-infrastructure-acquisition",{
        body:{
          action:"member_activation_verify_otp",
          setup_id:focused.setup_id,
          phase:otpPhase,
          code,
        },
      });
      if(error) throw error;
      if(payload?.error) throw new Error(String(payload.error));
      setOtpCode("");
      setOtpNotice(
        otpPhase==="CENTRAL"
          ? "RUNYI identity verified. A MEMBER project code was sent; enter it here next."
          : "MEMBER project verified. Tenant enrollment and activation completed.",
      );
      onRefresh();
    } catch(error) {
      setOtpError(error instanceof Error ? error.message : "The code could not be verified.");
    } finally {
      setOtpSubmitting(false);
    }
  };


  const submitConfirmationLink=async()=>{
    if(!supabase || !connected || !focused || !confirmationLinkRequired) {
      setConfirmationError("RUNYI connection is not ready.");
      return;
    }
    const link=confirmationLink.trim();
    if(!link || link.length>8192) {
      setConfirmationError("Paste the complete Supabase confirmation link.");
      return;
    }
    try {
      const parsed=new URL(link);
      if(parsed.protocol!=="https:") {
        setConfirmationError("The confirmation link must use HTTPS.");
        return;
      }
    } catch {
      setConfirmationError("Paste a valid confirmation link.");
      return;
    }

    setConfirmationSubmitting(true);
    setConfirmationError(null);
    try {
      const {data:payload,error}=await supabase.functions.invoke("runyi-infrastructure-acquisition",{
        body:{
          action:"member_activation_verify_confirmation_link",
          setup_id:focused.setup_id,
          confirmation_link:link,
        },
      });
      if(error) {
        const context=error as {context?:Response;message?:string};
        const response=context.context;
        const detail=response ? await response.clone().json().catch(()=>null) : null;
        throw new Error(String(detail?.detail ?? detail?.error ?? context.message ?? "The verification request failed."));
      }
      if(payload?.error) throw new Error(String(payload?.detail ?? payload.error));
      setConfirmationLink("");
      onRefresh();
    } catch(error) {
      setConfirmationError(error instanceof Error ? error.message : "The confirmation link could not be verified.");
    } finally {
      setConfirmationSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <ManualMembersWorkspace
        setups={setups}
        selected={focused}
        selectedSetupId={selectedSetupId}
        missingSelection={missingSelectedSetup}
        higherPriorityOverlayOpen={systemDetailsOpen}
        open={membersOpen}
        onOpenChange={setMembersOpen}
        onSelect={selectSetup}
        onOpenSetup={onOpenSetup}
        onRefresh={onRefresh}
        onMarketplace={onMarketplace}
        onAddMember={onAddMember}
        onRegisterExisting={onRegisterExisting}
        busy={busy}
      />
      {focused && (focused.interaction_contract?.authorization?.required === true || ["MEMBER_TENANT_ENROLLMENT","VERIFY_MEMBER_CONFIRMATION_LINK","ENTER_MEMBER_OTP","CONFIGURE_MEMBER_EMAIL_CHANNEL"].includes(String(focused.action_kind ?? "").toUpperCase())) ? (
        <UniversalAccountsSetupPanel supabase={supabase} connected={connected} setupId={focused.setup_id} compact onComplete={onRefresh} />
      ) : null}
      <section className="w-full min-w-0 max-w-none overflow-hidden border-y border-foreground/14 bg-black shadow-none sm:rounded-xl sm:border">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <p className="text-[8px] font-bold uppercase tracking-[0.22em] text-white/40">RUNYI DIGITAL DISPLAY</p>
            <p className="mt-0.5 text-[13px] font-semibold text-white">New Infrastructure</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full border px-2 py-1 text-[8px] font-semibold ${liveConnected?"border-cyan-400/25 bg-cyan-500/10 text-cyan-300":"border-amber-400/25 bg-amber-500/10 text-amber-200"}`}>● {liveConnected?"LIVE":"POLLING"}</span>
            <button type="button" onClick={onRefresh} disabled={busy} className="grid size-8 place-items-center rounded-full border border-white/12 bg-white/[0.05] text-white/70"><RefreshCw className={`size-3.5 ${busy?"animate-spin":""}`} /></button>
          </div>
        </div>

        <div className="min-h-[calc(100dvh-12rem)] max-h-none overflow-y-visible px-3 py-3 text-white sm:px-4">
          {!focused ? (
            <div className="flex min-h-[480px] flex-col">
              <div className="pt-8 text-center">
                <Radio className="mx-auto size-8 text-lime-300" />
                <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">{setups.length?"MANUAL MEMBER SELECTION":"WAITING FOR BACKEND"}</p>
                <h2 className="mt-2 text-xl font-semibold">{missingSelectedSetup?"Selected member unavailable":setups.length?"All Members":"No infrastructure setup"}</h2>
                <p className="mx-auto mt-2 max-w-[280px] text-[11px] leading-5 text-white/55">{setups.length?"Select a member in Manual Members above. RUNYI will not switch workspaces when another project refreshes.":"When RUNYI prepares infrastructure, its member registry and authentication will appear here."}</p>
                {setups.length>0&&<Button type="button" variant="outline" className="mt-4 rounded-xl border-lime-400/30 text-lime-200" onClick={()=>setMembersOpen(true)}>Choose Member</Button>}
              </div>
              <div className="mt-auto space-y-2 pb-2">
                <PhoneStatus label="Backend" value={display?.backend_online===false?"CHECK":"ONLINE"} />
                <PhoneStatus label="Realtime" value={liveConnected?"CONNECTED":"RECONNECTING"} />
                <PhoneStatus label="Last update" value={relativeTime(display?.last_sync_at)} />
                <Button type="button" variant="outline" className="mt-3 w-full rounded-2xl border-white/15 bg-white/[0.05] text-white hover:bg-white/[0.09]" onClick={onMarketplace}><Database className="size-4" />Marketplace</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-white/35">{focused.provider_key}</p>
                  <h2 className="mt-1 truncate text-xl font-semibold">{focused.display_name}</h2>
                  <p className="mt-1 text-[9px] text-white/38">{pretty(focused.profile?.logical_capability ?? focused.capability?.service_type ?? "Infrastructure")}</p>
                </div>
                <UniversalInfrastructureActions
                  supabase={supabase}
                  connected={connected}
                  target={{target_entity_key:`PROVIDER_CAPABILITY:${focused.provider_capability_key}`,display_name:focused.display_name,category_key:String(focused.capability?.metadata?.category_key ?? focused.profile?.resource_type ?? focused.capability?.service_type ?? "FUTURE_RESOURCE"),provider_key:focused.provider_key,logical_capability:focused.profile?.logical_capability ?? focused.capability?.logical_capability_hint ?? null,target_kind:"CAPABILITY",metadata:{provider_capability_key:focused.provider_capability_key}}}
                  applications={applications}
                  assignments={[]}
                />
              </div>

              <div className="mt-3 text-center">
                <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${focusedVisual.status}`}>{pretty(focused.live_stage)}</p>
                <p className="mx-auto mt-2 max-w-[300px] text-[10px] leading-5 text-white/45">{focused.live_summary}</p>
              </div>

              <div className="mt-3">
                <div className="flex justify-between text-[8px] text-white/35"><span>SETUP PROGRESS</span><span>{Math.round(progress)}%</span></div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className={`h-full rounded-full transition-all ${focusedVisual.progress}`} style={{width:`${Math.max(3,Math.min(100,progress))}%`}} /></div>
              </div>

              <button type="button" className="mt-3 flex min-h-9 w-full items-center justify-between rounded-xl border border-white/10 px-3 text-left text-[10px] font-semibold text-white/65" aria-expanded={setupDetailsOpen} onClick={() => setSetupDetailsOpen((value) => !value)}><span>Setup checks · {Math.round(progress)}% complete</span><ChevronRight className={`size-4 transition ${setupDetailsOpen ? "rotate-90" : ""}`} /></button>
              {setupDetailsOpen && <div className="mt-2 space-y-1.5">
                <PhoneStage label="Backend definition" state={stages?.backend_definition ?? "PENDING"} />
                <PhoneStage label="Authentication" state={stages?.authentication ?? "WAITING"} />
                <PhoneStage label="Credentials" state={stages?.credential ?? "WAITING"} />
                <PhoneStage label="Permissions" state={stages?.permissions ?? "WAITING"} />
                <PhoneStage label="Connection" state={stages?.provider_connection ?? "WAITING"} />
                <PhoneStage label="Adapter" state={stages?.execution_adapter ?? "PENDING"} />
                <PhoneStage label="Verification" state={stages?.verification ?? "WAITING"} />
              </div>}

              {focused.human_action_required && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">YOUR ACTION</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">{focused.digital_display?.next_human_action?.label ?? focused.live_summary}</p>
                  {marketplaceAction && (
                    <p className="mt-3 rounded-xl border border-lime-400/25 bg-lime-400/[0.045] px-3 py-2.5 text-[11px] leading-5 text-lime-200">
                      Member email is already verified. Continue with protected health revalidation and verified Marketplace registration; no new link or OTP is required.
                    </p>
                  )}
                  {memberVerification && (
                    <div className="mt-3 rounded-2xl border border-lime-400/25 bg-lime-400/[0.045] p-3">
                      <p className="mb-2 text-[10px] font-semibold text-lime-300">Email verification method</p>
                      <div className="grid grid-cols-2 gap-2">
                        {memberVerification.methods.map((method)=>(
                          <Button
                            key={method.kind}
                            type="button"
                            variant={verificationMethod===method.kind?"default":"outline"}
                            className="min-h-11 rounded-xl text-xs"
                            onClick={()=>setVerificationMethod(method.kind)}
                            disabled={busy || confirmationSubmitting || otpSubmitting || !["AVAILABLE","PROVIDER_CONFIGURATION_DEPENDENT"].includes(method.availability)}
                          >
                            {method.kind==="LINK"?"Email Link":"Email OTP"}
                          </Button>
                        ))}
                      </div>
                      {verificationMethod==="CODE" && (
                        <p className="mt-2 text-[10px] leading-4 text-white/60">
                          Numeric OTP requires a configured member-project email channel. If unavailable, choose Email Link.
                        </p>
                      )}
                    </div>
                  )}
                  {memberVerification && verificationMethod==="CODE" && !memberCodePending ? (
                    <div className="mt-3 space-y-2">
                      <Button type="button" className="min-h-12 w-full rounded-2xl" onClick={()=>onSendVerification(focused.setup_id,"CODE")} disabled={busy}>
                        {busy?<LoaderCircle className="size-4 animate-spin" />:<Mail className="size-4" />}Send Email OTP
                      </Button>
                    </div>
                  ) : memberVerification && verificationMethod==="LINK" && !confirmationLinkRequired ? (
                    <div className="mt-3 space-y-2">
                      <Button type="button" className="min-h-12 w-full rounded-2xl" onClick={()=>onSendVerification(focused.setup_id,"LINK")} disabled={busy}>
                        {busy?<LoaderCircle className="size-4 animate-spin" />:<Mail className="size-4" />}Send Confirmation Link
                      </Button>
                    </div>
                  ) : confirmationLinkRequired && verificationMethod==="LINK" ? (
                    <div className="mt-3 space-y-3">
                      <label htmlFor={`runyi-confirmation-link-${focused.setup_id}`} className="block text-[11px] font-semibold text-cyan-200">
                        Paste member-project confirmation link
                      </label>
                      <p className="text-[10px] leading-5 text-white/65">
                        Open the Supabase email for {focused.target_member_email ?? focused.target_account_ref ?? "the selected member account"}, copy its complete “Confirm email address” link, and paste it below.
                      </p>
                      <Textarea
                        id={`runyi-confirmation-link-${focused.setup_id}`}
                        value={confirmationLink}
                        onChange={(event)=>{
                          setConfirmationLink(event.target.value.slice(0,8192));
                          if(confirmationError) setConfirmationError(null);
                        }}
                        onKeyDown={(event)=>{
                          if(event.key==="Enter" && (event.ctrlKey || event.metaKey)) {
                            event.preventDefault();
                            if(!confirmationSubmitting) void submitConfirmationLink();
                          }
                        }}
                        placeholder="Paste the full https:// confirmation link here"
                        aria-label="Member-project confirmation URL"
                        className="min-h-32 w-full resize-y rounded-2xl border-cyan-400/35 bg-black/45 p-3 text-base leading-6 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-cyan-300"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="off"
                        spellCheck={false}
                        disabled={confirmationSubmitting}
                      />
                      {confirmationError && <p role="alert" className="rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-[11px] leading-5 text-red-200">{confirmationError}</p>}
                      <Button
                        type="button"
                        className="min-h-12 w-full rounded-2xl"
                        onClick={()=>void submitConfirmationLink()}
                        disabled={confirmationSubmitting || !confirmationLink.trim()}
                      >
                        {confirmationSubmitting?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}
                        {confirmationSubmitting ? "Verifying…" : "Verify Link"}
                      </Button>
                      <p className="text-[9px] leading-4 text-white/50">
                        The pasted link is sent only for immediate server-side verification and is not saved in RUNYI setup metadata or audit records.
                      </p>
                    </div>
                  ) : emailChannelRequired ? (
                    <div className="mt-3 space-y-2">
                      <div className="rounded-2xl border border-amber-400/15 bg-amber-400/[0.05] px-3 py-3">
                        <p className="text-[9px] font-semibold text-amber-200">Email Channel Required</p>
                        <p className="mt-1 text-[9px] leading-4 text-white/45">A custom email provider can still be configured for numeric OTPs, or RUNYI can switch this setup to secure confirmation-link verification.</p>
                      </div>
                      <Button
                        type="button"
                        className="min-h-11 w-full rounded-2xl"
                        onClick={onMarketplace}
                        disabled={busy}
                      >
                        <Mail className="size-4" />
                        Choose Email Provider
                      </Button>
                    </div>
                  ) : otpRequired ? (
                    <div className="mt-3 space-y-2">
                      <label className="block text-[8px] font-semibold uppercase tracking-[0.14em] text-white/40" htmlFor={`runyi-member-otp-${focused.setup_id}`}>
                        {otpPhase==="CENTRAL" ? "RUNYI email code" : "MEMBER project email code"}
                      </label>
                      <Input
                        id={`runyi-member-otp-${focused.setup_id}`}
                        value={otpCode}
                        onChange={(event)=>{
                          setOtpCode(event.target.value.replace(/\D/g,"").slice(0,8));
                          if(otpError) setOtpError(null);
                        }}
                        onKeyDown={(event)=>{
                          if(event.key==="Enter" && !otpSubmitting) {
                            event.preventDefault();
                            void submitMemberOtp();
                          }
                        }}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        aria-label={otpPhase==="CENTRAL" ? "RUNYI email verification code" : "Member project email verification code"}
                        pattern="[0-9]*"
                        maxLength={8}
                        placeholder="Enter 6–8 digit code"
                        className="h-12 rounded-2xl border-white/15 bg-black/30 text-center text-lg font-semibold tracking-[0.28em] text-white placeholder:text-[10px] placeholder:tracking-normal"
                        disabled={otpSubmitting || busy}
                        autoFocus
                      />
                      {otpError && <p className="text-[9px] leading-4 text-red-300">{otpError}</p>}
                      {otpNotice && <p className="text-[9px] leading-4 text-emerald-300">{otpNotice}</p>}
                      <Button
                        type="button"
                        className="min-h-11 w-full rounded-2xl"
                        onClick={()=>void submitMemberOtp()}
                        disabled={otpSubmitting || busy || otpCode.replace(/\D/g,"").length<6}
                      >
                        {otpSubmitting?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}
                        {otpSubmitting ? "Verifying…" : otpPhase==="CENTRAL" ? "Verify RUNYI Code" : "Verify Member Code"}
                      </Button>
                      <p className="text-center text-[8px] leading-4 text-white/30">The code is submitted directly for verification and is not stored in RUNYI setup metadata.</p>
                    </div>
                  ) : (
                    <Button type="button" className="mt-3 min-h-12 w-full rounded-2xl" onClick={()=>onContinueSetup(focused.setup_id)} disabled={busy}>
                      {busy?<LoaderCircle className="size-4 animate-spin" />:marketplaceAction?<Database className="size-4" />:<KeyRound className="size-4" />}
                      {marketplaceAction?"Finish Marketplace Activation":actionLabel}
                    </Button>
                  )}
                </div>
              )}

              {!focused.human_action_required && focused.digital_display?.backend_action_active && (
                <div className="mt-5 rounded-2xl border border-blue-400/18 bg-blue-500/[0.055] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-blue-300">BACKEND WORKING</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/55">{focused.digital_display?.backend_action_description ?? focused.live_summary}</p>
                </div>
              )}

              {setupDetailsOpen && <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.025] p-3">
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-white/35">PROVIDER TRUTH</p>
                <PhoneStatus label="Connection" value={pretty(focused.provider_truth?.state ?? "UNKNOWN")} />
                <PhoneStatus label="Intended account" value={focused.target_account_ref ?? focused.target_member_email ?? focused.provider_truth?.intended_account_ref ?? "—"} />
                <PhoneStatus label="Account" value={focused.provider_truth?.account_ref ?? "—"} />
                <PhoneStatus label="Project" value={focused.provider_truth?.project_ref ?? "—"} />
              </div>}

              <button type="button" className="mt-4 w-full text-center text-[10px] text-white/60 underline decoration-white/25 underline-offset-4" onClick={()=>onOpenSetup(focused.setup_id)}>
                Open {focused.display_name} setup details{setups.length>1?` · ${setups.length} active setups`:""}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="w-full min-w-0 max-w-none rounded-[18px] border border-foreground/8 bg-card p-3">
        <div className="flex items-center gap-2"><Activity className="size-4 text-primary" /><div><p className="text-[10px] font-semibold">Live activity</p><p className="text-[8px] text-foreground/35">Most recent backend events</p></div></div>
        <div className="mt-2 max-h-28 space-y-1 overflow-y-auto">
          {(data?.activity ?? []).length===0 ? <p className="rounded-xl border border-dashed border-foreground/10 px-3 py-2.5 text-[9px] text-foreground/35">No live activity yet.</p> : (data?.activity ?? []).slice(0,6).map((event)=><div key={String(event.event_id)} className="rounded-lg bg-foreground/[0.025] px-2.5 py-2 text-[9px] text-foreground/50"><span className="font-semibold">{event.actor ?? "RUNYI BACKEND"}</span> · {event.summary}</div>)}
        </div>
      </section>

      <button type="button" onClick={()=>setSystemDetailsOpen(true)} className="mx-auto flex w-full max-w-[430px] items-center justify-between rounded-[18px] border border-foreground/8 bg-card px-3.5 py-3 text-left">
        <div><p className="text-[10px] font-semibold">System Details</p><p className="mt-0.5 text-[9px] text-foreground/35">Architecture, security and responsibility guarantees</p></div><ChevronRight className="size-4 text-foreground/35" />
      </button>
      {systemDetailsOpen && <SystemDetailsSheet onClose={()=>setSystemDetailsOpen(false)} />}
      {role==="MEMBER" && <p className="w-full min-w-0 max-w-none rounded-xl border border-amber-500/15 bg-amber-500/[0.04] px-3 py-2 text-[10px] text-amber-200">Infrastructure authentication requires delegated infrastructure-management permission.</p>}
    </div>
  );
}

function PhoneStatus({label,value}:{label:string;value:string}) {
  const visual=stateVisual(value);
  return <div className={`mt-2 flex items-center justify-between rounded-xl border px-3 py-2 text-[9px] ${visual.row}`}><span className="text-white/40">{label}</span><span className={`font-semibold ${visual.status}`}>{value}</span></div>;
}

function PhoneStage({label,state}:{label:string;state:string}) {
  const Icon=statusIcon(state);
  const visual=stateVisual(state);
  return <div className={`flex items-center justify-between rounded-xl border px-3 py-2 transition-colors ${visual.row}`}><span className="text-[9px] text-white/50">{label}</span><span className={`flex items-center gap-1.5 text-[8px] font-semibold ${visual.status}`}><Icon className="size-3" />{pretty(state)}</span></div>;
}

function DigitalInfrastructureCard({
  item,supabase,connected,applications,busy,onOpenSetup,onMarketplace,
}:{
  item:NewInfrastructureLiveSetup;
  supabase:SupabaseClient|null;
  connected:boolean;
  applications:AppItem[];
  busy:boolean;
  onOpenSetup:(key:string)=>void;
  onMarketplace:()=>void;
}) {
  const stages=item.digital_display?.stages ?? {
    backend_definition:item.profile?"READY":"PENDING",
    authentication:item.connection?"VERIFIED":"WAITING",
    credential:item.connection?"VERIFIED":"WAITING",
    permissions:(item.missing_permissions ?? []).length?"ACTION_REQUIRED":"READY",
    provider_connection:item.connection?.health_state ?? "WAITING",
    execution_adapter:item.execution?.coverage_state ?? "CATALOGUED",
    verification:item.execution?.coverage_state==="VERIFIED"?"VERIFIED":"WAITING",
    marketplace:item.execution?.coverage_state==="VERIFIED"?"READY":"WAITING",
  };
  const progress=item.digital_display?.progress_percent ?? item.progress_percent ?? 0;
  const backendWorking=item.digital_display?.backend_action_active ?? false;
  const category=String(item.capability?.metadata?.category_key ?? item.profile?.resource_type ?? item.capability?.service_type ?? "FUTURE_RESOURCE");
  const nextAction=item.digital_display?.next_human_action?.label ?? item.live_summary;

  return (
    <article className="rounded-[22px] border border-foreground/8 bg-card p-4 shadow-sm">
      <div className="flex items-start gap-2">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={()=>onOpenSetup(item.setup_id)}>
          <div className="flex items-start gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-foreground/8 bg-foreground/[0.025] text-primary">
              {item.human_action_required?<KeyRound className="size-4" />:<Workflow className="size-4" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{item.display_name}</span>
              <span className="mt-0.5 block truncate text-[9px] uppercase tracking-wider text-foreground/35">{item.provider_key} · {pretty(category)}</span>
            </span>
            <Badge className={`border ${stateTone(item.live_stage)}`}>{pretty(item.live_stage)}</Badge>
          </div>
        </button>
        <UniversalInfrastructureActions
          supabase={supabase}
          connected={connected}
          target={{
            target_entity_key:`PROVIDER_CAPABILITY:${item.provider_capability_key}`,
            display_name:item.display_name,
            category_key:category,
            provider_key:item.provider_key,
            logical_capability:item.profile?.logical_capability ?? item.capability?.logical_capability_hint ?? null,
            target_kind:"CAPABILITY",
            metadata:{provider_capability_key:item.provider_capability_key},
          }}
          applications={applications}
          assignments={[]}
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-[9px] text-foreground/38">
        <span>Overall progress</span>
        <span className="font-semibold text-foreground/65">{Math.round(progress)}%</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]"><div className={`h-full rounded-full transition-all ${stateVisual(item.live_stage).progress}`} style={{width:`${Math.max(3,Math.min(100,progress))}%`}} /></div>

      <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
        <Stage label="Backend Definition" state={stages.backend_definition} />
        <Stage label="Authentication" state={stages.authentication} />
        <Stage label="Credential" state={stages.credential} />
        <Stage label="Permissions" state={stages.permissions} />
        <Stage label="Provider Connection" state={stages.provider_connection} />
        <Stage label="Execution Adapter" state={stages.execution_adapter} />
        <Stage label="Verification" state={stages.verification} />
        <Stage label="Marketplace" state={stages.marketplace} />
      </div>

      {item.human_action_required ? (
        <div className="mt-3 rounded-xl border border-orange-400/15 bg-orange-500/[0.04] p-3">
          <p className="text-[8px] font-bold uppercase tracking-wider text-orange-300">YOUR ACTION</p>
          <p className="mt-1 text-[10px] leading-4 text-foreground/58">{nextAction}</p>
          <Button type="button" size="sm" className="mt-2 rounded-xl" onClick={()=>onOpenSetup(item.setup_id)} disabled={busy}><KeyRound className="size-3.5" />Open Authentication</Button>
        </div>
      ) : backendWorking ? (
        <div className="mt-3 rounded-xl border border-sky-400/15 bg-sky-500/[0.035] p-3">
          <p className="text-[8px] font-bold uppercase tracking-wider text-blue-300">BACKEND WORKING</p>
          <p className="mt-1 text-[10px] leading-4 text-foreground/58">{item.digital_display?.backend_action_description ?? item.live_summary}</p>
        </div>
      ) : ["VERIFIED","MARKETPLACE_READY"].includes(item.live_stage) ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-lime-400/20 bg-lime-400/[0.05] p-3">
          <div><p className="text-[8px] font-bold uppercase tracking-wider text-lime-300">INFRASTRUCTURE READY</p><p className="mt-1 text-[10px] text-foreground/50">Provider access and execution support are verified.</p></div>
          <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={onMarketplace}>Marketplace</Button>
        </div>
      ) : null}

      <div className="mt-3 rounded-xl border border-foreground/8 bg-background/60 p-3">
        <div className="flex items-center justify-between gap-2"><p className="text-[8px] font-bold uppercase tracking-wider text-foreground/35">PROVIDER TRUTH</p><Badge className={`border ${stateTone(item.provider_truth?.state ?? "UNKNOWN")}`}>{pretty(item.provider_truth?.state ?? "UNKNOWN")}</Badge></div>
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[9px]">
          <Truth label="Intended" value={item.target_account_ref ?? item.target_member_email ?? item.provider_truth?.intended_account_ref ?? "—"} />
          <Truth label="Account" value={item.provider_truth?.account_ref ?? "—"} />
          <Truth label="Project" value={item.provider_truth?.project_ref ?? "—"} />
          <Truth label="Auth" value={pretty(item.provider_truth?.auth_mode ?? "—")} />
          <Truth label="Verified" value={relativeTime(item.provider_truth?.last_verified_at)} />
        </div>
      </div>
    </article>
  );
}

export function NewInfrastructureSetupSession({
  setup,requirements,busy,credentialOpen,credentialFields,credentialValues,onCredentialChange,onCredentialSubmit,onCredentialClose,onRequirementAction,onContinueSetup,onSendVerification,onSelectProject,onCreateProject,onRetryProjectRegistration,onVerify,onRefresh,onClose,
}:{
  setup:NewInfrastructureLiveSetup | null;
  requirements:NewInfrastructureLiveRequirement[];
  busy:boolean;
  credentialOpen:boolean;
  credentialFields:NewInfrastructureCredentialField[];
  credentialValues:Record<string,string>;
  onCredentialChange:(key:string,value:string)=>void;
  onCredentialSubmit:()=>void;
  onCredentialClose:()=>void;
  onRequirementAction:(requirement:NewInfrastructureLiveRequirement)=>void;
  onContinueSetup:(setupId:string)=>void;
  onSendVerification:(setupId:string,method:"CODE"|"LINK")=>void;
  onSelectProject:(projectRef:string)=>void;
  onCreateProject:(setupId:string,details:{organization_slug:string;project_name:string;region_group:"americas"|"emea"|"apac"})=>void;
  onRetryProjectRegistration:(setupId:string,projectRef:string)=>void;
  onVerify:()=>void;
  onRefresh:()=>void;
  onClose:()=>void;
}) {
  const [organizationSlug,setOrganizationSlug]=useState("");
  const [projectName,setProjectName]=useState("");
  const [regionGroup,setRegionGroup]=useState<"americas"|"emea"|"apac">("emea");
  const [createConfirmed,setCreateConfirmed]=useState(false);
  const [credentialFileName,setCredentialFileName]=useState<string | null>(null);
  const [credentialFileError,setCredentialFileError]=useState("");
  useEffect(() => {
    setOrganizationSlug("");
    setProjectName("");
    setRegionGroup("emea");
    setCreateConfirmed(false);
    setCredentialFileName("");
    setCredentialFileError("");
  },[setup?.setup_id]);
  useEffect(() => {
    if (!credentialOpen) {
      setCredentialFileName("");
      setCredentialFileError("");
    }
  },[credentialOpen]);


  if (!setup) return <div className="grid min-h-52 place-items-center"><LoaderCircle className="size-6 animate-spin text-primary" /></div>;

  const connectionReq=requirements.find((item)=>item.requirement_key==="provider_connection");
  const stages=setup.digital_display?.stages;
  const setupAction=String(setup.action_kind ?? setup.digital_display?.next_human_action?.kind ?? "").toUpperCase();
  const setupOwnedActions=["SELECT_PROJECT","CREATE_PROJECT","PREPARE_BOOTSTRAP","APPROVE_BOOTSTRAP","MEMBER_TENANT_ENROLLMENT","VERIFY_MEMBER_CONFIRMATION_LINK","ENTER_MEMBER_OTP","CONFIGURE_MEMBER_EMAIL_CHANNEL","RECONCILE_MARKETPLACE"];
  const action=setupOwnedActions.includes(setupAction)
    ? setupAction
    : String(connectionReq?.action_kind ?? setup.action_kind ?? "");
  const actionLabel=setup.digital_display?.next_human_action?.label ?? phoneActionLabel(action);
  const progress=setup.digital_display?.progress_percent ?? setup.progress_percent ?? 0;
  const setupVisual=setup.human_action_required
    ? stateVisual("ACTION_REQUIRED")
    : setup.digital_display?.backend_action_active
      ? stateVisual("BACKEND_WORKING")
      : stateVisual(setup.live_stage);

  return (
    <div className="space-y-3">
      <section className="w-full min-w-0 max-w-none overflow-hidden border-y border-foreground/14 bg-black shadow-none sm:rounded-xl sm:border">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <button type="button" onClick={credentialOpen?onCredentialClose:onClose} className="text-[10px] font-semibold text-white/60">‹ {credentialOpen?"Authentication":"Digital Display"}</button>
          <span className="text-[8px] font-bold uppercase tracking-[0.18em] text-white/30">RUNYI DISPLAY</span>
          <button type="button" onClick={onRefresh} disabled={busy} className="grid size-8 place-items-center rounded-full border border-white/12 bg-white/[0.05] text-white/60"><RefreshCw className={`size-3.5 ${busy?"animate-spin":""}`} /></button>
        </div>

        <div className="min-h-[calc(100dvh-16rem)] max-h-none overflow-y-visible px-4 py-4 text-white">
          {credentialOpen ? (
            <div>
              <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-orange-300">CREDENTIALS</p>
              <h2 className="mt-1 text-xl font-semibold">{setup.display_name}</h2>
              <p className="mt-1 text-[9px] text-white/38">{setup.provider_key} · secure backend activation</p>
              <p className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] p-3 text-[9px] leading-5 text-white/45">Values are submitted directly to RUNYI's protected credential backend. Plaintext credentials are cleared after submission and never enter the Digital Display status stream.</p>

              <div className="mt-4 space-y-3">
                {credentialFields.length===0 && <div className="rounded-2xl border border-orange-400/20 bg-orange-500/[0.06] p-3 text-[9px] leading-5 text-orange-100">The backend has not published a secure credential schema for this requirement. RUNYI will not request arbitrary secrets.</div>}
                {credentialFields.map((field)=>(
                  <div key={field.key} className="block">
                    <span className="text-[9px] font-semibold text-white/55">{field.label}{field.required?" *":""}</span>
                    {field.type==="secret_textarea" && field.key==="private_key" ? (
                      <div className="mt-1.5 space-y-2">
                        <GithubPemCredentialInput
                          selectedName={credentialFileName}
                          onSelectedNameChange={setCredentialFileName}
                          onPemLoaded={(pem)=>onCredentialChange(field.key,pem)}
                          onError={setCredentialFileError}
                          className="h-12 rounded-2xl border-white/12 bg-white/[0.04] text-[12px] text-white"
                        />
                        {credentialFileError ? (
                          <div className="rounded-xl border border-red-400/20 bg-red-500/[0.05] px-3 py-2 text-[9px] text-red-200">{credentialFileError}</div>
                        ) : null}
                        <details className="rounded-xl border border-white/8 bg-white/[0.025] px-3 py-2">
                          <summary className="cursor-pointer text-[9px] text-white/45">Paste PEM manually instead</summary>
                          <Textarea value={credentialValues[field.key] ?? ""} onChange={(event)=>{setCredentialFileName(null);setCredentialFileError("");onCredentialChange(field.key,event.target.value);}} className="mt-2 min-h-28 rounded-xl border-white/12 bg-black/30 font-mono text-[12px] text-white" autoComplete="off" spellCheck={false} />
                        </details>
                      </div>
                    ) : field.type==="secret_textarea" ? (
                      <Textarea value={credentialValues[field.key] ?? ""} onChange={(event)=>onCredentialChange(field.key,event.target.value)} className="mt-1.5 min-h-32 rounded-2xl border-white/12 bg-white/[0.04] font-mono text-[13px] text-white" autoComplete="off" spellCheck={false} />
                    ) : (
                      <Input type={field.type==="secret"?"password":"text"} value={credentialValues[field.key] ?? ""} onChange={(event)=>onCredentialChange(field.key,event.target.value)} className="mt-1.5 h-12 rounded-2xl border-white/12 bg-white/[0.04] text-[16px] text-white" autoComplete="off" spellCheck={false} />
                    )}
                  </div>
                ))}
              </div>

              <Button type="button" className="mt-5 min-h-12 w-full rounded-2xl" onClick={onCredentialSubmit} disabled={busy||credentialFields.length===0}>
                {busy?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}Verify & Connect
              </Button>
            </div>
          ) : (
            <div>
              <div className="text-center">
                <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-white/35">{setup.provider_key}</p>
                <h2 className="mt-1 text-xl font-semibold">{setup.display_name}</h2>
                <p className={`mt-4 text-[10px] font-bold uppercase tracking-[0.18em] ${setupVisual.status}`}>{pretty(setup.live_stage)}</p>
                <p className="mx-auto mt-2 max-w-[300px] text-[10px] leading-5 text-white/45">{setup.live_summary}</p>
              </div>

              <div className="mt-5">
                <div className="flex justify-between text-[8px] text-white/35"><span>SETUP PROGRESS</span><span>{Math.round(progress)}%</span></div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className={`h-full rounded-full ${setupVisual.progress}`} style={{width:`${Math.max(3,Math.min(100,progress))}%`}} /></div>
              </div>

              <div className="mt-5 space-y-1.5">
                <PhoneStage label="Definition" state={stages?.backend_definition ?? "PENDING"} />
                <PhoneStage label="Authentication" state={stages?.authentication ?? connectionReq?.state ?? "WAITING"} />
                <PhoneStage label="Credentials" state={stages?.credential ?? "WAITING"} />
                <PhoneStage label="Permissions" state={stages?.permissions ?? "WAITING"} />
                <PhoneStage label="Connection" state={stages?.provider_connection ?? "WAITING"} />
                <PhoneStage label="Adapter" state={stages?.execution_adapter ?? "PENDING"} />
                <PhoneStage label="Verification" state={stages?.verification ?? "WAITING"} />
              </div>

              {(setup.missing_permissions ?? []).length>0 && (
                <div className="mt-4 rounded-2xl border border-orange-400/15 bg-orange-500/[0.05] p-3">
                  <p className="text-[8px] font-bold uppercase tracking-[0.15em] text-orange-300">Required permissions</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">{setup.missing_permissions!.map((item)=><span key={item} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-[8px] text-white/55">{item}</span>)}</div>
                </div>
              )}

              {action==="SELECT_PROJECT" && (setup.project_options ?? []).length>0 && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">SELECT PROJECT</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">RUNYI verified this Supabase account. Choose the discovered project that belongs to this member setup.</p>
                  <div className="mt-3 space-y-2">
                    {(setup.project_options ?? []).map((project)=>(
                      <button
                        key={project.ref}
                        type="button"
                        onClick={()=>onSelectProject(project.ref)}
                        disabled={busy}
                        className="w-full rounded-2xl border border-white/12 bg-white/[0.04] px-3 py-3 text-left transition hover:bg-white/[0.08] disabled:opacity-50"
                      >
                        <span className="block text-[11px] font-semibold text-white">{project.name || project.ref}</span>
                        <span className="mt-1 block text-[8px] text-white/40">{project.ref}{project.region ? ` · ${project.region}` : ""}{project.status ? ` · ${project.status}` : ""}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {action==="CREATE_PROJECT" && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">CREATE SUPABASE PROJECT</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">Authorization is verified for {setup.target_account_ref ?? setup.target_member_email}. Choose this account's provider-discovered organization, name and region group. RUNYI submits the request through its governed, Free-only project provisioner. Paid creation remains blocked.</p>
                  <label className="mt-3 block text-[10px] text-white/75">Supabase organization</label>
                  <select value={organizationSlug} onChange={(event)=>setOrganizationSlug(event.target.value)} disabled={busy} className="mt-1 w-full min-h-11 rounded-xl border border-white/15 bg-black px-3 text-[12px] text-white">
                    <option value="">Select the verified account's organization</option>
                    {(setup.organization_options ?? []).map((org)=><option key={org.slug} value={org.slug}>{org.name || org.slug}</option>)}
                  </select>
                  <label className="mt-3 block text-[10px] text-white/75">Project name</label>
                  <Input type="text" maxLength={80} value={projectName} onChange={(event)=>setProjectName(event.target.value)} placeholder={String(setup.metadata?.member_label ?? setup.display_name).replaceAll("_"," ")} disabled={busy} className="mt-1 min-h-11 border-white/15 bg-black text-white" />
                  <label className="mt-3 block text-[10px] text-white/75">Region group</label>
                  <select value={regionGroup} onChange={(event)=>setRegionGroup(event.target.value as "americas"|"emea"|"apac")} disabled={busy} className="mt-1 min-h-11 w-full rounded-xl border border-white/15 bg-black px-3 text-[12px] text-white">
                    <option value="emea">Europe / Middle East / Africa</option>
                    <option value="americas">Americas</option>
                    <option value="apac">Asia Pacific</option>
                  </select>
                  <label className="mt-3 flex items-start gap-2 text-[10px] leading-5 text-white/75">
                    <input type="checkbox" checked={createConfirmed} onChange={(event)=>setCreateConfirmed(event.target.checked)} className="mt-1 size-4 shrink-0" disabled={busy} />
                    <span>I confirm this organization and Free-only project request. Do not create a paid project or duplicate an existing provider project.</span>
                  </label>
                  <Button type="button" className="mt-3 min-h-12 w-full rounded-2xl" onClick={()=>onCreateProject(setup.setup_id,{organization_slug:organizationSlug,project_name:projectName.trim() || String(setup.metadata?.member_label ?? setup.display_name).replaceAll("_"," ").slice(0,80),region_group:regionGroup})} disabled={busy || !organizationSlug || !createConfirmed || !((setup.organization_options ?? []).some((org)=>org.slug===organizationSlug))}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<Database className="size-4" />}Create Project (Free Only)
                  </Button>
                  <p className="mt-2 text-[9px] leading-4 text-white/45">Project creation is not immediate activation. RUNYI waits for provider truth before registration and member bootstrap.</p>
                </div>
              )}

              {setup.provider_key.toUpperCase()==="SUPABASE"
                && setup.backend_stage.toUpperCase()==="VERIFYING"
                && Boolean(setup.target_project_ref)
                && Number(setup.progress_percent)>=70
                && Number(setup.progress_percent)<75
                && !setup.action_kind && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">PROJECT INITIALIZATION</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">Supabase created this exact project. RUNYI is waiting for its publishable API key and verified registry entry; retry registration without creating another project or repeating account authentication.</p>
                  <Button type="button" className="mt-3 min-h-12 w-full rounded-2xl"
                    onClick={()=>onRetryProjectRegistration(setup.setup_id,String(setup.target_project_ref))}
                    disabled={busy}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<RefreshCw className="size-4" />}
                    Complete Project Registration
                  </Button>
                  <p className="mt-2 text-[9px] leading-4 text-white/45">If Supabase is still initializing, the current project is preserved. Retry later; do not press Create Project again.</p>
                </div>
              )}

              {action==="PREPARE_BOOTSTRAP" && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">CANONICAL BOOTSTRAP</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">The provider-discovered project is registered. This step validates the pinned GitHub artifact, project lineage and baseline security state. It does not apply schema changes.</p>
                  <Button type="button" className="mt-3 min-h-11 w-full rounded-2xl" onClick={()=>onRequirementAction({requirement_key:"setup_bootstrap",requirement_type:"APP_PERMISSION",label:"Prepare canonical member bootstrap",state:"ACTION_REQUIRED",required:true,action_kind:"PREPARE_BOOTSTRAP"})} disabled={busy}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}{phoneActionLabel(action)}
                  </Button>
                </div>
              )}

              {action==="APPROVE_BOOTSTRAP" && (
                <div className="mt-5 rounded-2xl border border-orange-400/25 bg-orange-500/[0.08] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">OWNER APPROVAL REQUIRED</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">The canonical UNINSTALLED → Control Tower v4 artifact and preflight are ready. Approving this action permits RUNYI to apply that pinned migration to the selected member project and independently verify it. The central RUNYI project is excluded.</p>
                  <Button type="button" className="mt-3 min-h-11 w-full rounded-2xl" onClick={()=>onRequirementAction({requirement_key:"setup_bootstrap_approval",requirement_type:"APP_PERMISSION",label:"Approve and apply canonical member bootstrap",state:"ACTION_REQUIRED",required:true,action_kind:"APPROVE_BOOTSTRAP"})} disabled={busy}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}{phoneActionLabel(action)}
                  </Button>
                </div>
              )}

              {action==="RECONCILE_MARKETPLACE" && (
                <div className="mt-5 rounded-2xl border border-lime-400/25 bg-lime-400/[0.045] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-lime-300">FINAL MARKETPLACE SYNC</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/70">
                    Member email and authenticated first-use validation passed. Finish verified infrastructure/service registration without repeating authentication.
                  </p>
                  <Button type="button" className="mt-3 min-h-12 w-full rounded-2xl" onClick={()=>onContinueSetup(setup.setup_id)} disabled={busy}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<Database className="size-4" />}
                    Finish Marketplace Activation
                  </Button>
                  <Button type="button" variant="outline"
                    className="mt-2 min-h-11 w-full rounded-2xl border-amber-300/45 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20"
                    onClick={()=>window.dispatchEvent(new CustomEvent("runyi:show-infrastructure-approvals"))}>
                    <ShieldCheck className="size-4" />Review Protected Approvals
                  </Button>
                  <p className="mt-2 text-[10px] leading-4 text-white/50">
                    Owner-controlled member health or session repair approvals appear here without repeating email verification.
                  </p>
                </div>
              )}

              {action==="MEMBER_TENANT_ENROLLMENT" && (
                <div className="mt-5 rounded-2xl border border-lime-400/25 bg-lime-400/[0.045] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-lime-300">MEMBER ENROLLMENT</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/70">Control Tower v4 is verified for {String(setup.metadata?.member_label ?? "this member").replaceAll("_"," ")}. Verify the member-project email identity for {setup.target_member_email ?? setup.target_account_ref ?? "the selected account"} to complete enrollment.</p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button type="button" className="min-h-12 rounded-2xl text-xs" onClick={()=>onSendVerification(setup.setup_id,"LINK")} disabled={busy}>
                      <Mail className="size-4" />Email Link
                    </Button>
                    <Button type="button" variant="outline" className="min-h-12 rounded-2xl text-xs" onClick={()=>onSendVerification(setup.setup_id,"CODE")} disabled={busy}>
                      <KeyRound className="size-4" />Email OTP
                    </Button>
                  </div>
                  <p className="mt-2 text-[10px] text-white/50">Numeric OTP requires a configured member-project email channel. Link verification remains available if not configured.</p>
                </div>
              )}

              {action==="VERIFY_MEMBER_CONFIRMATION_LINK" && (
                <div className="mt-5 rounded-2xl border border-lime-400/25 bg-lime-400/[0.045] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-lime-300">VERIFY MEMBER EMAIL</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/70">Check {setup.target_member_email ?? setup.target_account_ref ?? "the member account"} for the Supabase confirmation email. Open RUNYI's secure form to paste and verify the complete link.</p>
                  <Button type="button" className="mt-3 min-h-12 w-full rounded-2xl" onClick={onClose} disabled={busy}>
                    <ShieldCheck className="size-4" />Open Secure Verification Form
                  </Button>
                </div>
              )}

              {connectionReq && connectionReq.state!=="SATISFIED" && !["SELECT_PROJECT","CREATE_PROJECT","PREPARE_BOOTSTRAP","APPROVE_BOOTSTRAP","MEMBER_TENANT_ENROLLMENT"].includes(action) && (
                <div className="mt-5 rounded-2xl border border-orange-400/20 bg-orange-500/[0.07] p-3.5">
                  <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-300">YOUR ACTION</p>
                  <p className="mt-1.5 text-[10px] leading-5 text-white/60">{connectionReq.label}</p>
                  <Button type="button" className="mt-3 min-h-11 w-full rounded-2xl" onClick={()=>onRequirementAction(connectionReq)} disabled={busy}>
                    {busy?<LoaderCircle className="size-4 animate-spin" />:<KeyRound className="size-4" />}{actionLabel}
                  </Button>
                </div>
              )}

              <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.025] p-3">
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-white/35">PROVIDER TRUTH</p>
                <PhoneStatus label="Connection" value={pretty(setup.provider_truth?.state ?? "UNKNOWN")} />
                <PhoneStatus label="Auth" value={pretty(setup.provider_truth?.auth_mode ?? "—")} />
                <PhoneStatus label="Intended account" value={setup.target_account_ref ?? setup.target_member_email ?? setup.provider_truth?.intended_account_ref ?? "—"} />
                <PhoneStatus label="Account" value={setup.provider_truth?.account_ref ?? "—"} />
                <PhoneStatus label="Project" value={setup.provider_truth?.project_ref ?? "—"} />
              </div>

              <Button type="button" variant="outline" className="mt-4 w-full rounded-2xl border-white/12 bg-white/[0.03] text-white" onClick={onVerify} disabled={busy}><Cable className="size-4" />Verify Provider Access</Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function phoneActionLabel(action:string) {
  switch(action.toUpperCase()) {
    case "BROWSER_AUTHORIZATION": return "Continue in Browser";
    case "ADD_API_TOKEN": return "Enter API Token";
    case "ADD_TOKEN": return "Enter Access Token";
    case "ADD_SERVICE_ACCOUNT": return "Enter Service Account";
    case "ADD_GITHUB_APP": return "Connect GitHub App";
    case "ADD_CLIENT_CREDENTIAL": return "Enter Client Credentials";
    case "CONNECT_MCP": return "Connect MCP";
    case "GRANT_PERMISSIONS": return "Grant Permissions";
    case "SELECT_ACCOUNT": return "Select Account";
    case "SELECT_PROJECT": return "Select Project";
    case "CREATE_PROJECT": return "Create Project";
    case "PREPARE_BOOTSTRAP": return "Prepare Bootstrap";
    case "APPROVE_BOOTSTRAP": return "Approve & Bootstrap";
    case "MEMBER_TENANT_ENROLLMENT": return "Complete Member Enrollment";
    case "VERIFY": return "Verify & Connect";
    default: return "Continue Authentication";
  }
}

function DigitalMetric({label,value,detail,attention=false}:{label:string;value:string;detail:string;attention?:boolean}) {
  return <div className="min-w-0 bg-card px-2 py-2.5 sm:p-3"><p className="truncate text-[7px] font-bold uppercase tracking-wider text-foreground/30">{label}</p><div className="mt-0.5 flex min-w-0 items-end justify-between gap-1"><p className={`text-xl font-semibold leading-none sm:text-2xl ${attention?"text-amber-200":""}`}>{value}</p><span className="truncate text-[7px] font-semibold text-foreground/35">{detail}</span></div></div>;
}

function StatusChip({label,value}:{label:string;value:string}) {
  return <div className="min-w-0 bg-card px-2 py-2"><p className="truncate uppercase tracking-wider text-foreground/28">{label}</p><p className="mt-0.5 truncate font-semibold text-foreground/58">{value}</p></div>;
}

function SectionTitle({icon:Icon,title,detail}:{icon:LucideIcon;title:string;detail:string}) {
  return <div className="flex items-start gap-2.5"><span className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.07] text-primary"><Icon className="size-4" /></span><div><p className="text-[11px] font-semibold">{title}</p><p className="mt-0.5 text-[9px] text-foreground/35">{detail}</p></div></div>;
}

function Stage({label,state}:{label:string;state:string}) {
  const Icon=statusIcon(state);
  const visual=stateVisual(state);
  return <div className={`flex items-center justify-between gap-2 rounded-xl border px-2.5 py-2 ${visual.row}`}><span className="text-[9px] text-foreground/50">{label}</span><span className={`flex min-w-0 items-center gap-1 text-right text-[8px] font-semibold ${visual.status}`}><Icon className="size-3 shrink-0" />{pretty(state)}</span></div>;
}

function Truth({label,value}:{label:string;value:string}) {
  return <div className="min-w-0"><span className="text-foreground/30">{label}</span><p className="truncate text-foreground/55">{value}</p></div>;
}

function SystemDetailsSheet({onClose}:{onClose:()=>void}) {
  const items:Array<{title:string;summary:string;detail:string;icon:LucideIcon}> = [
    {
      title:"Owner / Human",
      summary:"Human-only authentication and approval.",
      detail:"Provider login, MFA, consent, credentials, permissions, scopes and provider account/project selection remain human-controlled.",
      icon:KeyRound,
    },
    {
      title:"ChatGPT / Backend",
      summary:"Infrastructure engineering and automation.",
      detail:"Provider logic, requirement profiles, execution adapters, verification contracts and automated backend work are prepared by ChatGPT/coding agents and RUNYI backend.",
      icon:ServerCog,
    },
    {
      title:"Backend decides requirements",
      summary:"The app renders authoritative backend requirements.",
      detail:"Credential types, OAuth methods, scopes, permissions, account/project selection and verification rules come from backend requirement profiles rather than client-side hard-coding.",
      icon:ShieldCheck,
    },
    {
      title:"Secrets stay backend-side",
      summary:"Realtime receives status, never secret values.",
      detail:"Tokens, keys, passwords and service credentials use protected activation. Plaintext secrets are not persisted in the Digital Display or returned in realtime payloads.",
      icon:KeyRound,
    },
    {
      title:"Provider truth stays separate",
      summary:"Desired state is not treated as actual state.",
      detail:"RUNYI keeps provider actual state separate until connection, probe and verification authorities confirm it.",
      icon:Eye,
    },
    {
      title:"Flow remains executor",
      summary:"ADD INFRASTRUCTURE governs; Flow mutates providers.",
      detail:"ADD INFRASTRUCTURE plans and authorizes. Governed Flow/provider adapters perform provider mutation and verification.",
      icon:Workflow,
    },
  ];

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] flex items-end justify-center bg-black/68 backdrop-blur-[3px] sm:items-center sm:p-4"
      role="presentation"
      data-runyi-global-modal="system-details"
      onMouseDown={(event)=>{if(event.currentTarget===event.target) onClose();}}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="runyi-system-details-title"
        className="flex max-h-[calc(100dvh-3.5rem)] w-full flex-col overflow-hidden rounded-t-[26px] border border-foreground/10 bg-background shadow-2xl sm:max-h-[82dvh] sm:max-w-lg sm:rounded-[24px]"
      >
        <div className="shrink-0 flex items-center justify-between gap-3 border-b border-foreground/8 bg-background/95 px-4 py-3.5 backdrop-blur">
          <div className="min-w-0">
            <p id="runyi-system-details-title" className="text-sm font-semibold">System Details</p>
            <p className="mt-0.5 text-[9px] text-foreground/38">Architecture, security and responsibility guarantees</p>
          </div>
          <Button type="button" variant="outline" size="icon" className="size-8 rounded-xl" onClick={onClose} aria-label="Close System Details">
            <X className="size-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain px-3 py-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          {items.map((item)=>(
            <SystemDetailRow key={item.title} {...item} />
          ))}
        </div>
      </section>
    </div>,
    document.body,
  );
}

function SystemDetailRow({title,summary,detail,icon:Icon}:{title:string;summary:string;detail:string;icon:LucideIcon}) {
  return (
    <details className="group overflow-hidden rounded-xl border border-foreground/8 bg-foreground/[0.015]">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/[0.07] text-primary"><Icon className="size-3.5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold">{title}</span>
          <span className="mt-0.5 block truncate text-[9px] text-foreground/38">{summary}</span>
        </span>
        <ChevronRight className="size-3.5 shrink-0 text-foreground/30 transition group-open:rotate-90" />
      </summary>
      <div className="border-t border-foreground/8 px-3 py-2.5 text-[9px] leading-4 text-foreground/45">{detail}</div>
    </details>
  );
}

function Panel({icon:Icon,title,children}:{icon:LucideIcon;title:string;children:ReactNode}) {
  return <div className="rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-4"><div className="mb-3 flex items-center gap-2"><span className="grid size-8 place-items-center rounded-xl bg-primary/[0.07] text-primary"><Icon className="size-4" /></span><p className="text-[11px] font-semibold">{title}</p></div><div className="space-y-2">{children}</div></div>;
}

function StatusLine({label,value}:{label:string;value:string}) {
  const Icon=statusIcon(value);
  return <div className="flex items-center justify-between gap-3 rounded-xl border border-foreground/8 bg-background px-3 py-2"><span className="text-[10px] text-foreground/45">{label}</span><span className="flex min-w-0 items-center gap-1.5 text-right text-[10px] font-semibold"><Icon className="size-3.5 shrink-0" />{value}</span></div>;
}
